import { useEffect, useMemo, useRef, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  AddIcon,
  CloseIcon,
  MicIcon,
  NoImageProvidedIcon,
  TickIcon
} from "../../Assets/assets";

import { isValidArray, isValidObject } from "../../Services/validators";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import Modal from "../Modal/Modal";
import "./CarouselAndAudioPlayer.css";
import useVoiceRecorder, {
  voiceRecorderTimeLimit
} from "../../CustomHooks/useVoiceRecorder";

export default function CarouselAndAudioPlayer(props) {
  const [close, setClose] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth(); // initial measure

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleRemoveImage = (indexToRemove) => {
    if (typeof props.onRemoveImage === "function") {
      props.onRemoveImage(indexToRemove);
      return;
    }
    setClose(false);
  };

  useEffect(() => {
    setModalOpen(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (close) {
      props.onImageClick(null);
    }
    // eslint-disable-next-line
  }, [close]);

  const getImageSrc = (proofSrc, add) => {
    if (add && proofSrc instanceof File) {
      try {
        return URL.createObjectURL(proofSrc);
      } catch (error) {
        console.error("Error creating object URL:", error);
        return null;
      }
    }
    return proofSrc;
  };
  return (
    <div className="inherit-parent-width inherit-parent-height">
      <div
        ref={containerRef}
        className={`inherit-parent-height inherit-parent-width display-flex flex-direction-column flex-align-items-center flex-justify-content-center`}
        data-cy={`${props.dataCy}-CAROUSEL-COMPONENT`}
      >
        {isValidArray(props?.fileSrc?.images) && (
          <div className="cursor-pointer inherit-parent-width display-flex">
            <Carousel
              autoPlay={false}
              centerSlidePercentage={50}
              infiniteLoop={false}
              onClickItem={(index) => {
                if (!close) {
                  setSelectedImageIndex(index);
                  setModalOpen(true);
                  props.onImageClick(props?.fileSrc?.images?.[index]);
                }
              }}
              selectedItem={
                props.add ? props?.fileSrc?.images?.length - 1 : null
              }
              width={`${width}px`}
              showArrows={true}
              showStatus={false}
              showIndicators={false}
              showThumbs={false}
            >
              {props?.fileSrc?.images?.map(
                (proofSrc, index) =>
                  proofSrc && (
                    <div
                      key={index}
                      className="display-flex flex-align-items-center flex-justify-content-center"
                    >
                      <div className="inherit-parent-width">
                        {props.add && (
                          <div
                            data-cy={`IMAGE-${index}-DELETE-ICON`}
                            className="display-flex flex-justify-content-center flex-align-items-center cursor-pointer"
                            onClick={() => {
                              handleRemoveImage(index);
                            }}
                            onMouseEnter={() => {
                              setClose(true);
                            }}
                            onMouseLeave={() => {
                              setClose(false);
                            }}
                            style={{
                              borderRadius: "100%",
                              width: 18,
                              zIndex: 4,
                              height: 18,
                              backgroundColor: "black",
                              position: "absolute",
                              top: 6,
                              right: 6
                            }}
                          >
                            <CloseIcon
                              height="10"
                              width="10"
                              color="var(--tertiaryColor1)"
                            />
                          </div>
                        )}
                        <img
                          className={`${
                            props?.fileSrc?.images?.length > 0
                              ? "margin-right-large"
                              : ""
                          } inherit-parent-width`}
                          src={getImageSrc(proofSrc, props.add)}
                          alt={`item-${index}`}
                          style={{
                            height: "200px",
                            objectFit: "cover"
                          }}
                          data-cy={`${props.dataCy}-proof-${index + 1}`}
                        />
                      </div>
                    </div>
                  )
              )}
            </Carousel>
          </div>
        )}

        {(!isValidObject(props?.fileSrc) ||
          !isValidArray(props?.fileSrc?.images)) &&
          props.add === undefined && (
            <div
              className="display-flex flex-justify-content-center inherit-parent-width flex-align-items-center"
              data-cy={`${props.dataCy}-no-image-provided-icon`}
            >
              <div
                className="display-flex inherit-parent-width flex-justify-content-center flex-align-items-center background-color-grey-C2C2C2 cursor-no-drop"
                style={{
                  height: "200px",
                  backgroundColor: "#1c1c1c"
                }}
              >
                {props.assetsLoading ? (
                  <div className="background-color-white padding-larger border-radius-default ">
                    <div
                      className="circular-loader"
                      style={{ height: "24px", width: "24px" }}
                      data-cy="circular-loader"
                    />
                  </div>
                ) : (
                  <div className="display-flex flex-direction-column flex-align-items-center">
                    <NoImageProvidedIcon width="40" height="40" />
                    <div
                      style={{
                        color: "#F3F3F3"
                      }}
                      className="padding-top-large font-size-small"
                    >
                      IMAGE NOT PROVIDED
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>

      {isValidObject(props?.fileSrc) &&
        typeof props?.fileSrc?.audio === "string" &&
        props.add === undefined && (
          <AudioPlayer
            className={`inherit-parent-width padding-vertical-large ${
              typeof props.fileSrc?.audio !== "string"
                ? "cursor-no-drop"
                : "cursor-pointer"
            }`}
            src={
              isValidObject(props?.fileSrc) &&
              typeof props.fileSrc?.audio === "string"
                ? props.fileSrc?.audio
                : ""
            }
            theme={props.theme}
            dataCy={`${props.dataCy}-AUDIO-PLAYER`}
          />
        )}

      {typeof props?.fileSrc?.audio !== "string" && props.add === undefined && (
        <div
          className={`inherit-parent-width padding-vertical-large text-align-center font-color-tertiaryColor6 padding-top-large font-size-small
            ${
              typeof props.fileSrc?.audio !== "string"
                ? "cursor-no-drop"
                : "cursor-pointer"
            }`}
        >
          AUDIO NOT PROVIDED
        </div>
      )}

      {props.add && (
        <div className="display-flex inherit-parent-width padding-top-larger">
          {props?.fileSrc?.images?.length <= 4 && (
            <>
              <AddImage
                proofType={props.proofType}
                setFile={props.setFile}
                file={props.file}
                setErrorStatus={props.setErrorStatus}
              />
              <div style={{ padding: "8px" }} />
            </>
          )}

          {[null, undefined].includes(props?.fileSrc?.audio) && (
            <AddAudio
              isAudioRecoding={props.isAudioRecoding}
              setFile={props.setFile}
              file={props.file.audio ? props.file : props?.fileSrc}
              setErrorStatus={props.setErrorStatus}
              onRemoveAudio={props.onRemoveAudio}
            />
          )}

          {props?.fileSrc?.audio instanceof Blob && (
            <AddAudio
              isAudioRecoding={props.isAudioRecoding}
              setFile={props.setFile}
              file={{ image: props.file.image, audio: props.fileSrc?.audio }}
              setErrorStatus={props.setErrorStatus}
              onRemoveAudio={props.onRemoveAudio}
            />
          )}
        </div>
      )}

      <Modal
        show={modalOpen}
        canIgnore={true}
        onClose={() => {
          setModalOpen(false);
        }}
        width="inherit-parent-width"
        maxWidth="inherit-parent-width"
        background="background-color-tertiaryColor2"
        boxShadow="true"
        borderRadius="false"
        height=""
      >
        <div className="display-flex flex-justify-content-center flex-align-items-center inherit-parent-width padding-larger">
          {isValidArray(props?.fileSrc?.images) &&
            props?.fileSrc?.images[selectedImageIndex] && (
              <div
                key={selectedImageIndex}
                className="display-flex flex-align-items-center flex-justify-content-center inherit-parent-width"
              >
                <img
                  className="inherit-parent-width"
                  src={getImageSrc(
                    props?.fileSrc?.images[selectedImageIndex],
                    props.add
                  )}
                  alt={`full-size-${selectedImageIndex}`}
                  data-cy={`modal-full-size-image-${selectedImageIndex}`}
                  style={{
                    objectFit: "contain"
                  }}
                />
              </div>
            )}
        </div>
      </Modal>
    </div>
  );
}

const AddImage = (props) => {
  return (
    <form
      className=""
      onChange={(event) => {
        if (event.target.files) {
          if (
            ["image/png", "image/jpeg", "image/jpg"].includes(
              event.target.files[0]?.type
            ) === false
          ) {
            return props.setErrorStatus({
              code: "custom",
              message: "Kindly upload a valid File"
            });
          }

          if (
            ["image/png", "image/jpeg", "image/jpg"].includes(
              event.target.files[0]?.type
            ) === true
          ) {
            props.setFile({
              ...(props.file || {}),
              image: [
                ...(isValidArray(props.file?.image) ? props.file.image : []),
                URL.createObjectURL(event.target.files[0])
              ]
            });
          }
        }
      }}
    >
      <label>
        <input
          type="file"
          accept=".jpg,.jpeg,.png"
          name="fileUploaderInput"
          {...(props.proofType === "live" ? { capture: "environment" } : {})}
          onClick={(event) => {
            event.target.value = "";
          }}
          className="visibility-hidden position-absolute"
        />
        <div
          style={{
            height: "50px",
            width: "50px",
            border: "dashed 2px #6E6E6E",
            borderRadius: "4px"
          }}
          onClick={() => {}}
          className="cursor-pointer display-flex flex-justify-content-center flex-align-items-center"
        >
          <div
            style={{
              height: "20px",
              width: "20px"
            }}
            data-cy="ADD-IMAGE-ICON"
            key="add-icon"
          >
            <AddIcon width="20" height="20" color="var(--tertiaryColor6)" />
          </div>
        </div>
      </label>
    </form>
  );
};

const AddAudio = (props) => {
  const [show, setShow] = useState(false);
  const [control, setControl] = useState(null);
  const [counter, setCounter] = useState(null);
  const [running, setRunning] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState({
    audioBlob: false,
    audioURL: ""
  });

  const { startRecording, stopRecording, saveRecording } = useVoiceRecorder();

  useEffect(() => {
    let counterTimer;
    if (
      counter !== null &&
      typeof counter === "number" &&
      counter <= voiceRecorderTimeLimit &&
      running
    ) {
      counterTimer = setTimeout(() => {
        setCounter(counter + 1);
      }, 1000);
    } else {
      return () => clearTimeout(counterTimer);
    }
    if (counter === voiceRecorderTimeLimit) {
      setControl("stop");
      voiceRecorderHandler();
    }
    // eslint-disable-next-line
  }, [counter]);

  useEffect(() => {
    if (recordedAudio?.audioURL) {
      setControl(null);
    }
  }, [recordedAudio?.audioURL]);

  useEffect(() => {
    if (props.file && props.file.audio) {
      setRecordedAudio({
        audioBlob: props.file.audio,
        audioURL:
          props.file.audio instanceof Blob
            ? URL.createObjectURL(props.file.audio)
            : null
      });
      setControl(null);
    } else {
      setRecordedAudio({
        audioBlob: false,
        audioURL: ""
      });
    }
    // eslint-disable-next-line
  }, [props.file]);

  const voiceRecorderHandler = async () => {
    try {
      if (control === null || control === "cancel") {
        setControl("start");
        setCounter(0);
        setRunning(true);

        const res = await startRecording();
        if (res === false) {
          /**
           * voice recorder returns false is microphone is detected or any other issues in recording
           */
          setShow(false);
          setControl(null);
        }
      } else if (control === "start") {
        setControl("stop");
        setShow(false);
        setCounter(null);
        setRunning(false);
        const resBlob = await saveRecording();
        setRecordedAudio({
          audioBlob: resBlob,
          audioURL: URL.createObjectURL(resBlob)
        });

        const audioFile = new File([resBlob], `${+new Date()}.mp3`, {
          type: "audio/mp3"
        });

        props.setFile({
          ...props.file,
          audio: audioFile
        });

        props.setClearAudio && props.setClearAudio(false);
      } else if (control === "stop") {
        setControl("cancel");
        setRecordedAudio({
          audioBlob: false,
          audioURL: ""
        });
      }
    } catch (error) {
      props.setErrorStatus({
        code: "custom",
        message: "Microphone is not connected"
      });
    }
  };

  useEffect(() => {
    if (typeof props.isAudioRecoding === "function") {
      props.isAudioRecoding(running);
    }
    // eslint-disable-next-line
  }, [running]);

  const shouldShowAudioPlayer = useMemo(() => {
    if (recordedAudio?.audioURL && show === false) {
      return true;
    } else {
      return false;
    }
  }, [recordedAudio?.audioURL, show]);

  return (
    <div className="inherit-parent-width display-flex flex-align-items-center">
      {!show && !recordedAudio?.audioURL && (
        <div
          data-cy="ADD-AUDIO-ICON"
          style={{
            height: "50px",
            width: "50px",
            border: "dashed 2px #6E6E6E",
            borderRadius: "4px"
          }}
          onClick={() => {
            setRecordedAudio({
              audioBlob: false,
              audioURL: ""
            });
            props.setFile({
              ...props.file,
              audio: null
            });
            setRunning(false);
            voiceRecorderHandler();
            setShow(true);
          }}
          className="cursor-pointer display-flex flex-justify-content-center flex-align-items-center"
        >
          <div className="display-flex flex-align-items-center">
            <div className="flex-place-children-page-center  border-radius-50-percentage">
              <MicIcon color="var(--tertiaryColor6)" />
            </div>
          </div>
        </div>
      )}

      {show && !recordedAudio?.audioURL && (
        <div
          style={{
            height: "50px",
            border: "solid 2px var(--tertiaryColor)",
            borderRadius: "4px",
            backgroundColor: "var(--tertiaryColor3)"
          }}
          className="inherit-parent-width display-flex flex-justify-content-space-between flex-align-items-center padding-left-medium padding-right-medium"
        >
          <div className="width-fit-content display-flex flex-align-items-center">
            <div
              style={{
                width: "28px",
                height: "28px"
              }}
            >
              <VoiceLoading />
            </div>
            <div
              className="padding-left-large white-space-nowrap font-color-tertiaryColor6"
              style={{ width: 50 }}
            >
              {control === "start"
                ? `00:${counter?.toString().length === 1 ? "0" : ""}${counter}`
                : `00:00`}
            </div>
          </div>
          <div className="display-flex">
            <div
              data-cy="SAVE-AUDIO-ICON"
              style={{
                width: "20px",
                height: "20px"
              }}
              onClick={() => {
                voiceRecorderHandler();
              }}
              className="cursor-pointer"
            >
              <TickIcon color="var(--tertiaryColor6)" />
            </div>
            <div
              data-cy="DELETE-AUDIO-ICON"
              style={{
                width: "20px",
                height: "20px"
              }}
              onClick={async () => {
                await stopRecording();
                setRecordedAudio({
                  audioBlob: false,
                  audioURL: ""
                });
                props.setFile({
                  ...props.file,
                  audio: null
                });
                setRunning(false);
                setShow(false);
                setControl(null);
              }}
              className="margin-left-default cursor-pointer"
            >
              <CloseIcon height="20" width="20" color="var(--tertiaryColor6)" />
            </div>
          </div>
        </div>
      )}

      {shouldShowAudioPlayer && (
        <AudioPlayer
          theme={props.theme}
          closeOnClick={() => {
            if (typeof props.onRemoveAudio === "function") {
              props.onRemoveAudio();
            }
            setRecordedAudio({
              audioBlob: false,
              audioURL: ""
            });
            props.setFile({
              ...props.file,
              audio: null
            });
            setShow(false);
          }}
          src={
            typeof recordedAudio.audioURL === "string"
              ? recordedAudio.audioURL
              : ""
          }
        />
      )}
    </div>
  );
};

const VoiceLoading = () => {
  return (
    <div className="inherit-parent-width inherit-parent-height display-flex flex-align-items-center flex-justify-content-center">
      {[...Array(4)].map((_data, i) => (
        <div key={i} className="bar" />
      ))}
    </div>
  );
};
